/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrUpdateSystemPromptsAPI } from "Api/systemPromptsAPI";
import PromptFragmentsValueTextArea from "views/admin/tables/prompt-fragments/components/create-or-update-prompt-fragments/components/promptFragmentsValueTextArea/promptFragmentsValueTextArea";

const FormPopup = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  initialFormData,
}) => {
  const { setFetchSystemPrompts } = useGlobalContext();
  const { formData, setFormData, initialFormState } = useGlobalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [initialDataSnapshot, setInitialDataSnapshot] = useState({});

  useEffect(() => {
    if (isOpen && isEditMode && initialFormData) {
      const normalizedCategory = initialFormData.category?.toUpperCase() || "";

      const updatedData = {
        ...initialFormData,
        status: initialFormData.status ? "active" : "inactive",
        category: normalizedCategory,
      };

      setFormData(updatedData);
      setInitialDataSnapshot(updatedData);
    } else if (isOpen && !isEditMode) {
      setFormData(initialFormState);
      setInitialDataSnapshot(initialFormState);
    }
  }, [isOpen, isEditMode, initialFormData]);

  useEffect(() => {
    const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(initialDataSnapshot);
    const hasRequiredFields =
      formData.key?.trim() &&
      formData.version?.trim() &&
      formData.comment?.trim() &&
      formData.value?.trim();

    setIsChanged(hasChanges && hasRequiredFields);
  }, [formData, initialDataSnapshot]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.key || !formData.version) {
      toast.error("Key and Version are required.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...formData,
      status: formData.status === "active",
      value: formData.value,
    };

    console.log("Submitting payload:", payload);

    const token = localStorage.getItem("jwtToken");

    try {
      const response = await createOrUpdateSystemPromptsAPI(
        payload,
        setIsSubmitting,
        token
      );

      if (response) {
        const successMessage = formData?.id
          ? "System prompt updated successfully"
          : "System prompt created successfully";
        toast.success(successMessage);

        onSubmit(response);
        onClose();
        setFormData(initialFormState);
        setFetchSystemPrompts(true);
      }
    } catch (error) {
      console.error("Submit Error:", error);

      const duplicateMsg =
        "A System Prompt with this key and version already exists";

      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error creating/updating System prompt.";

      if (apiMessage === duplicateMsg) {
        toast.error(duplicateMsg);
      } else {
        toast.error(apiMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialFormState);
    onClose();
  };

  const handleFormDataWithTextAreaValue = useCallback(
    (newTextValue) => {
      setFormData({
        ...formData,
        value: newTextValue,
      });
    },
    [formData, setFormData]
  );

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="relative max-h-[90vh] w-4/5 overflow-auto rounded-xl bg-white shadow-xl">
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
        >
          <IoMdClose size={24} />
        </button>
        <form
          onSubmit={handleSubmit}
          className="h-full w-full p-6 dark:!bg-gray-10 "
        >
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label
                  htmlFor="key"
                  className="mb-2 block font-bold text-gray-700 dark:text-white"
                >
                  <span className="text-red-600 ">*</span>
                  Key:
                </label>
                <input
                  id="key"
                  type="text"
                  name="key"
                  autoComplete="off"
                  value={formData.key || ""}
                  onChange={handleChange}
                  className="focus:border-transparent w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                  disabled={isSubmitting || isEditMode}
                  required
                />
              </div>

              <div className="flex-1">
                <label
                  htmlFor="category"
                  className="mb-2 block font-bold text-gray-700 dark:text-white"
                >
                  <span className="text-red-600">*</span>
                  Category:
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category || ""}
                  onChange={handleChange}
                  className="focus:border-transparent w-full rounded-md border border-gray-300 px-3 py-2 
      focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                  disabled={isSubmitting}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="ZENVOYA_APP">ZENVOYA_APP</option>
                  <option value="BACKEND">BACKEND</option>
                </select>
              </div>

              <div className="flex-1">
                <label
                  htmlFor="version"
                  className="mb-2 block font-bold text-gray-700 dark:text-white "
                >
                  <span className="text-red-600">*</span>
                  Version:
                </label>
                <input
                  id="version"
                  type="text"
                  name="version"
                  autoComplete="off"
                  value={formData.version || ""}
                  onChange={handleChange}
                  className="focus:border-transparent w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                  <span className="text-red-600">*</span>
                  Status:
                </label>
                <div className="flex items-center">
                  <label className="mr-4 dark:!bg-gray-10 dark:text-white">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={formData.status === "active"}
                      onChange={handleChange}
                      className="relative top-0.5 mr-1 cursor-pointer dark:text-gray-900"
                      disabled={isSubmitting}
                    />
                    Active
                  </label>
                  <label className="dark:!bg-gray-10 dark:text-white">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={formData.status === "inactive"}
                      onChange={handleChange}
                      className="relative top-0.5 mr-1 cursor-pointer "
                      disabled={isSubmitting}
                    />
                    In-Active
                  </label>
                </div>
              </div>
              <div className="flex-1">
                <label
                  htmlFor="comment"
                  className="mb-2 block font-bold text-gray-700 dark:text-white"
                >
                  <span className="text-red-600">*</span>
                  Comment:
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData?.comment}
                  onChange={handleChange}
                  className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                  disabled={isSubmitting}
                  required
                ></textarea>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-full max-w-full">
                <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                  <span className="text-red-600">*</span>
                  Value:
                </label>
                <PromptFragmentsValueTextArea
                  className="focus:border-transparent h-[40vh] max-h-[50vh] w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                  handleChange={handleChange}
                  setFormDataWithTextAreaValue={handleFormDataWithTextAreaValue}
                  textAreaValue={formData?.value || ""}
                />
              </div>
            </div>

            <div className="my-4 mb-6 flex justify-end space-x-4">
              <button
                type="submit"
                className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                  isSubmitting || !isChanged
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-green-600 hover:bg-green-500"
                }`}
                disabled={isSubmitting || !isChanged}
              >
                {isSubmitting ? "Submitting..." : isEditMode ? "Update" : "Add"}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormPopup;
